import { HOME_MSG } from "@genbaryoku/contract";
import { AreaIcon, areaOfPath, backLabelOf, type Area } from "./AreaIcon.jsx";
import { useLabels } from "../labels.js";
/**
 * 戻るボタン（#98、2026-09-29 射場さん、docs/37）。
 *   ・#240（docs/94 B-2・★4 C案、2026-10-26）：［← 行き先の絵 行き先］の枠のあるボタン（高さ 40）。一覧・詳細は題の左（同じ行）、帳票は頭の帯の左上。
 *     文字は行き先の短い名前＝書類は種類＋番号（見積書 Q-0001）・一覧は「〜一覧」・{project}・{customer}の詳細は種類だけ・ホーム（AreaIcon.tsx の backLabelOf）。
 *     乗せると全部の名前（「案件 27年度 …に戻る」＝title と読み上げ）。絵は左のメニューと同じ（行き先の分野＝areaOfPath）。スマホ版・767px 以下は前の形（‹ と文字）
 *   ・（前の形）題（h1）の左上、題のすぐ上の行に「‹ 行き先の名前」（文字だけのボタンの形のリンク。読み上げは「○○に戻る」）
 *   ・行き先：アプリの中で直前に居た画面があればそこ（navigate(-1)＝ブラウザの「戻る」と同じ。タブ・絞り込み・スクロールもそのまま）。
 *     無いとき（URL を直接開いた・メールのリンク・再読み込みのあと）は、その画面が渡す「上の画面」（docs/37 §2 の表）
 *   ・画面の名前は各画面が usePageName で登録する（題と同じ）。作ったあとの replace は前の画面を引き継ぐ（新しい案件 → 案件の画面の「‹」は案件の一覧）
 *   ・入れかけ（dirty）のときは「入れた内容は消えます」の小さな確かめ（主役は「続ける」、Esc で閉じる）。自動で保存する画面は確かめない
 *     （自動で保存する設定の画面でも、送れていない直し＝会社名が空・API に断られた ときは確かめる。S-00008。画面の中のリンクは useLeaveGuard）
 *   ・サイドメニューで開く画面（今日・案件・見積書・請求書・入金・顧客・設定）には置かない（名前の登録だけする）
 *
 * しくみ：ブラウザの履歴の1つ1つ（location.key）ごとに「1つ前の履歴」と「パス」を覚える（このタブの中だけ・メモリの中）。
 *   PUSH＝いまの画面が1つ前／REPLACE＝置き換えた画面の「1つ前」を引き継ぐ／POP（戻る・進む）＝覚えているものを使う。再読み込みで消える
 */
import { useEffect, useLayoutEffect, useRef, useState, type MouseEvent, type ReactNode } from "react";
import { Link, useLocation, useNavigate, useNavigationType, type Location, type NavigationType } from "react-router";
import { useSettingsPane } from "../lib/settingsPane.js";
import { recordNav } from "../lib/trail.js";

type Entry = { path: string; prev: string | null };
const entries = new Map<string, Entry>();
/** 画面の名前（題と同じ）と、戻るボタンに出す短い名前（書類の種類＋番号など。#240） */
const names = new Map<string, { name: string; short: string | null }>();
const listeners = new Set<() => void>();
/** いま表示している履歴の key（BackTracker が描いたあとに進める） */
let currentKey: string | null = null;

/** この履歴を覚える（何度呼んでも同じ。描く途中でも呼べるように、覚えてあれば何もしない） */
function ensure(loc: Location, type: NavigationType): Entry {
  const had = entries.get(loc.key);
  if (had) return had;
  const prev = type === "PUSH" ? currentKey : type === "REPLACE" ? (currentKey ? (entries.get(currentKey)?.prev ?? null) : null) : null;
  const e: Entry = { path: loc.pathname + loc.search, prev: prev === loc.key ? null : prev };
  entries.set(loc.key, e);
  return e;
}

/** App の BrowserRouter の中に1つ置く。画面が変わるたびに「いまの履歴」を進める */
export function BackTracker() {
  const loc = useLocation();
  const type = useNavigationType();
  ensure(loc, type);
  useLayoutEffect(() => {
    currentKey = loc.key;
    noteHistoryIndex(); // ブラウザの戻る・進むで動いた幅を出すため（離れるときの確かめ。#177）
    // 問い合わせの「？」に添える足あと（開いた画面。#144）
    recordNav(loc.pathname);
  }, [loc.key, loc.pathname]);
  return null;
}

/**
 * 画面の名前を登録する（題と同じ。読み込み中など、まだ無ければ null のままでよい）。
 * short＝ここへ戻るボタンに出す短い名前（#240：書類は「見積書 Q-0001」のように種類＋番号。省けば backLabelOf が決める＝一覧は「〜一覧」）
 */
export function usePageName(name: string | null | undefined, short?: string | null) {
  const loc = useLocation();
  useEffect(() => {
    if (!name) return;
    const had = names.get(loc.key);
    if (had && had.name === name && had.short === (short ?? null)) return;
    names.set(loc.key, { name, short: short ?? null });
    listeners.forEach((f) => f());
  }, [loc.key, name, short]);
}

/**
 * いま開いている画面の名前をその場で付け直す（#267 の続き、2026-10-29）。
 *   帳票の発行が通ったら、移る直前に番号つきの名前（「請求書 I-0001」）を付ける。移った先（送信の画面）の戻るボタンはこの名前を出す。
 *   前は画面が答えを読み直して usePageName で付け直す前に移ることがあり、戻るボタンが番号なし（「請求書」）になった
 */
export function namePageNow(name: string, short?: string | null) {
  if (!currentKey) return;
  names.set(currentKey, { name, short: short ?? null });
  listeners.forEach((f) => f());
}

/** いま開いている画面の名前（問い合わせに添える。#124 の 3a）。まだ登録が無ければ null */
export function currentPageName(): string | null {
  return currentKey ? (names.get(currentKey)?.name ?? null) : null;
}

/** 入れかけの見分け：いまの値が元の値と違うか（JSON でくらべる） */
export const differs = (a: unknown, b: unknown): boolean => JSON.stringify(a) !== JSON.stringify(b);

export type BackTarget = { to: string; label: string };
export type BackNav = {
  /** ボタンの字（行き先の短い名前。#240：書類は種類＋番号・一覧は「〜一覧」） */
  label: string;
  /** 行き先の全部の名前（乗せたときと読み上げ「○○に戻る」） */
  full: string;
  /** 行き先の分野（ボタンの絵・左のメニューと同じ） */
  area: Area | null;
  /** 行き先（href。直前の画面があればそのパス） */
  to: string;
  /** 直前の画面へ戻るか（false なら上の画面へ） */
  hasPrev: boolean;
  /** 押したとき（入れかけなら確かめを出す） */
  request: (e?: MouseEvent) => void;
  /** 確かめ（入れかけのときだけ中身がある）。使う画面が描く */
  dialog: ReactNode;
};

/**
 * 戻る先を決める。fallback＝直前の画面が無いときの「上の画面」。dirty＝入れかけ（押すと確かめる）
 * BackButton・作成画面の藍の帯の「‹」・下の「やめる」で同じものを使う
 */
export function useBackNav(fallback: BackTarget, dirty = false): BackNav {
  const loc = useLocation();
  const type = useNavigationType();
  const nav = useNavigate();
  const [, bump] = useState(0);
  const [asking, setAsking] = useState(false);
  useEffect(() => {
    const f = () => bump((n) => n + 1);
    listeners.add(f);
    return () => void listeners.delete(f);
  }, []);
  const L = useLabels();
  const me = ensure(loc, type);
  const prevKey = me.prev;
  const prev = prevKey ? entries.get(prevKey) : undefined;
  const hasPrev = !!prev;
  const named = hasPrev ? names.get(prevKey!) : undefined;
  const full = hasPrev ? (named?.name ?? "前の画面") : fallback.label;
  const to = hasPrev ? prev!.path : fallback.to;
  const label = backLabelOf(to, full, named?.short, L);
  const area = areaOfPath(to);
  const go = () => {
    setAsking(false);
    if (hasPrev) {
      allowLeave(); // 確かめ済み（か確かめが要らない）。ブラウザの戻ると同じ道（popstate）を通るので、離れるときの確かめで止めない
      nav(-1);
    } else nav(fallback.to);
  };
  const request = (e?: MouseEvent) => {
    e?.preventDefault();
    if (dirty) return setAsking(true);
    // 自動で保存する画面（useLeaveGuard を登録した画面。#177）：送れていない直しは確かめ、送る前・送っている最中は送り終えてから戻る
    const b = leaveBlockers();
    if (b.unsent) return setAsking(true);
    if (b.pending.length) {
      void settleGuards(b.pending).then((bad) => (bad ? setAsking(true) : go()));
      return;
    }
    go();
  };
  const dialog = asking ? <LeaveConfirm onStay={() => setAsking(false)} onLeave={go} /> : null;
  return { label, full, area, to, hasPrev, request, dialog };
}

/**
 * 入れかけの確かめ（「雛形を削除」と同じ形の小さな確かめ。主役は「続ける」、脇は「破棄」＝直しを捨てて離れる。Esc・外を押すと閉じて続ける）。
 * S-00009：ボタンは「続ける」と「破棄」の2つに揃えた（前は戻るとき「戻る」・リンクで移るとき「移る」）。
 * 振込口座のシート（#155）も、保存していない直しのまま取消・Esc・後ろを押したときにこれを出す
 */
export function LeaveConfirm({ onStay, onLeave }: { onStay: () => void; onLeave: () => void }) {
  useEffect(() => {
    const k = (e: KeyboardEvent) => {
      if (e.key === "Escape") onStay();
    };
    document.addEventListener("keydown", k);
    return () => document.removeEventListener("keydown", k);
  }, [onStay]);
  return (
    <div className="dim" onClick={(e) => e.target === e.currentTarget && onStay()}>
      <div className="dlg" role="alertdialog" aria-label="入れた内容は消えます" aria-describedby="leave-desc" data-testid="leave-confirm">
        <div className="dh">入れた内容は消えます</div>
        <div className="db">
          <p id="leave-desc" style={{ margin: 0 }}>
            保存していない内容は残りません。
          </p>
        </div>
        <div className="df">
          <button type="button" className="btn primary" autoFocus onClick={onStay} data-testid="leave-stay">
            続ける
          </button>
          <button type="button" className="btn" onClick={onLeave} data-testid="leave-go">
            破棄
          </button>
        </div>
      </div>
    </div>
  );
}

/* ---- 離れるときの確かめ（自動保存の共通の仕組み、#177。lib/saveQueue.tsx の useSaveQueue が使う。設定の画面の useAutoSave も） ---- */

/**
 * 離れる前に見るもの。boolean だけ渡す画面（設定の画面）は unsent だけ。
 * 送る前・送っている最中（pending）は、離れる前に送り終えるのを待ってから移る（確かめは出さない）。送れなかったら確かめる
 */
export type LeaveWatch = {
  /** 送れていない直し（断られた・つながらない・画面で分かる誤り）。離れる前に「続ける／破棄」 */
  unsent: boolean;
  /** 送る前・送っている最中の直しがある */
  pending?: () => boolean;
  /** ページを閉じると消える直し（keepalive で送れない）。閉じる前にブラウザの確かめ（beforeunload） */
  atRisk?: () => boolean;
  /** 送り終えるのを待つ。保存できたら true */
  settle?: () => Promise<boolean>;
};
/** 離れようとした先：画面の中のリンク／ブラウザの戻る・進む（delta＝履歴をいくつ動いたか。戻るは -1） */
type LeaveTarget = { kind: "link"; to: string } | { kind: "pop"; delta: number };
type Guard = { watch: () => LeaveWatch; ask: (t: LeaveTarget) => void; go: (t: LeaveTarget) => void };
/** いま開いている画面が登録した確かめ（請求書は 明細＋紙の上 の2つ。登録した順） */
const guards: Guard[] = [];
/**
 * 離れるときの確かめを止める（#189：自動ログアウト・セッション切れ。書きかけは端末に退避してから止めの画面・ログインの画面へ移る＝
 * 「続ける／破棄」・ブラウザの確かめで止めない）。ページを読み込み直すまで戻さない
 */
let released = false;
export function releaseLeaveGuards(): void {
  released = true;
}

/** 離れる前に止めるもの：送れていない直しのある確かめ（あれば先に）と、送り終えるのを待つ確かめ */
function leaveBlockers(): { unsent: Guard | null; pending: Guard[] } {
  let unsent: Guard | null = null;
  const pending: Guard[] = [];
  if (released) return { unsent, pending };
  for (const g of guards) {
    const w = g.watch();
    if (w.unsent) unsent ??= g;
    else if (w.pending?.()) pending.push(g);
  }
  return { unsent, pending };
}
/** 送り終えるのを待つ。送れなかった確かめを返す（みな保存できたら null） */
async function settleGuards(gs: Guard[]): Promise<Guard | null> {
  for (const g of gs) {
    const s = g.watch().settle;
    if (s && !(await s())) return g;
  }
  return null;
}

/**
 * 画面の中のリンク（左のメニュー・設定の左の列・「振込口座 ›」の行など）：document の click を捕まえて（React の Link より先）止める。
 * 新しいタブ（⌘・Ctrl・中ボタン・target）・ほかのサイト・同じ画面・「‹ 戻る」（useBackNav が見る）・自分で確かめるリンク（data-leave="own"＝PDF で確認 など）は止めない
 */
function onDocClick(e: globalThis.MouseEvent) {
  if (!guards.length || e.defaultPrevented || e.button !== 0 || e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) return;
  const a = e.target instanceof Element ? e.target.closest<HTMLAnchorElement>("a[href]") : null;
  if (!a || (a.target && a.target !== "_self") || a.hasAttribute("download") || a.classList.contains("backbtn") || a.dataset.leave === "own") return;
  const url = new URL(a.href, window.location.href);
  if (url.origin !== window.location.origin) return;
  if (url.pathname + url.search === window.location.pathname + window.location.search) return;
  const b = leaveBlockers();
  if (!b.unsent && !b.pending.length) return;
  e.preventDefault();
  e.stopPropagation();
  const t: LeaveTarget = { kind: "link", to: url.pathname + url.search + url.hash };
  if (b.unsent) return b.unsent.ask(t);
  void settleGuards(b.pending).then((bad) => (bad ? bad.ask(t) : b.pending[0]!.go(t)));
}
/** ページを閉じる・再読み込み：送れていない直し・閉じると消える直しがあればブラウザの確かめ */
function onUnload(e: BeforeUnloadEvent) {
  if (released) return;
  if (guards.some((g) => {
    const w = g.watch();
    return w.unsent || !!w.atRisk?.();
  })) e.preventDefault();
}
if (typeof document !== "undefined") document.addEventListener("click", onDocClick, true);

/*
 * ブラウザの戻る・進む（⌘[・iPhone のスワイプ・戻るボタン。#177）：URL が動いたあとの popstate を、react-router より先に受ける
 * （このファイルは App より先に読まれるので、react-router の BrowserRouter より先に登録される。capture でも先にする）。
 * 止めるときは react-router に渡さず（stopImmediatePropagation）、history.go で元の画面の履歴に戻してから確かめる。
 * 「破棄」・送り終えたあとは、通す印を立てて同じ幅だけ動かし直す。動いた幅は react-router が history.state に入れる idx の差
 */
/** いま表示している画面の履歴の番号（BackTracker が画面の変わるたびに入れる） */
let curIdx: number | null = null;
/** アプリが自分で戻る（「‹ 戻る」・確かめの「破棄」）：次の1回の popstate は止めない（2 秒まで） */
let allowUntil = 0;
/** 止めて元に戻したときの popstate（react-router に渡さない。2 秒まで） */
let restoring = 0;
let restoringUntil = 0;
const idxOf = (st: unknown): number | null => {
  const i = (st as { idx?: unknown } | null)?.idx;
  return typeof i === "number" ? i : null;
};
function noteHistoryIndex() {
  curIdx = idxOf(window.history.state);
}
function allowLeave() {
  allowUntil = Date.now() + 2000;
}
function onPopState(e: PopStateEvent) {
  if (restoring > 0 && Date.now() < restoringUntil) {
    restoring--;
    e.stopImmediatePropagation();
    return;
  }
  restoring = 0;
  if (Date.now() < allowUntil) {
    allowUntil = 0;
    return;
  }
  if (!guards.length) return;
  const idx = idxOf(e.state);
  if (idx == null || curIdx == null || idx === curIdx) return;
  const b = leaveBlockers();
  if (!b.unsent && !b.pending.length) return;
  e.stopImmediatePropagation();
  const delta = idx - curIdx;
  restoring++;
  restoringUntil = Date.now() + 2000;
  window.history.go(-delta);
  const t: LeaveTarget = { kind: "pop", delta };
  if (b.unsent) return b.unsent.ask(t);
  void settleGuards(b.pending).then((bad) => (bad ? bad.ask(t) : void popAway(delta)));
}
/** 止めた戻る・進むを、元に戻し終えてから同じ幅だけ動かし直す（通す印を立てて） */
async function popAway(delta: number) {
  for (let k = 0; k < 80 && restoring > 0 && Date.now() < restoringUntil; k++) await new Promise((r) => window.setTimeout(r, 25));
  allowLeave();
  window.history.go(delta);
}
if (typeof window !== "undefined") window.addEventListener("popstate", onPopState, true);
function addGuard(g: Guard) {
  guards.push(g);
  if (guards.length === 1) window.addEventListener("beforeunload", onUnload);
}
function removeGuard(g: Guard) {
  const i = guards.indexOf(g);
  if (i >= 0) guards.splice(i, 1);
  if (!guards.length) window.removeEventListener("beforeunload", onUnload);
}

/**
 * 自動で保存する画面が、離れるときの確かめを登録する（S-00008 の続き・#177）。登録しているあいだ、
 *   ・画面の中のリンク：送れていない直しがあれば「入れた内容は消えます」（主役は「続ける」、「破棄」でそのリンクへ）。
 *     送る前・送っている最中なら送り終えるのを待ってから移る（送れなければ同じ確かめ）
 *   ・「‹ 戻る」（useBackNav）・ブラウザの戻る・進む（⌘[・iPhone のスワイプ）：同じ
 *   ・ページを閉じる・再読み込み：送れていない直し・閉じると消える直しがあればブラウザの確かめ（beforeunload）
 * active は boolean（送れていない直しがあるか。設定の画面）か LeaveWatch（useSaveQueue）。返す dialog を画面が描く
 */
export function useLeaveGuard(active: boolean | LeaveWatch): { dialog: ReactNode } {
  const nav = useNavigate();
  const navRef = useRef(nav);
  navRef.current = nav;
  const activeRef = useRef(active);
  activeRef.current = active;
  const [target, setTarget] = useState<LeaveTarget | null>(null);
  const leaveTo = (t: LeaveTarget) => (t.kind === "link" ? navRef.current(t.to) : void popAway(t.delta));
  useEffect(() => {
    const g: Guard = {
      watch: () => {
        const a = activeRef.current;
        return typeof a === "boolean" ? { unsent: a } : a;
      },
      ask: (t) => setTarget(t),
      go: (t) => leaveTo(t),
    };
    addGuard(g);
    return () => removeGuard(g);
  }, []);
  const dialog = target ? (
    <LeaveConfirm
      onStay={() => setTarget(null)}
      onLeave={() => {
        setTarget(null);
        leaveTo(target);
      }}
    />
  ) : null;
  return { dialog };
}

/** 戻るボタンの全部の名前のうち、短い名前に入っていない分（「案件」に対する「 27年度 …」）。自前の戻るボタン（作成画面の帯・工程表）も同じ題にする（#240） */
export const backExtra = (back: Pick<BackNav, "label" | "full">) => (back.full && !back.label.includes(back.full) ? ` ${back.full}` : "");
/** 乗せたときの題「案件 27年度 …に戻る」 */
export const backTitle = (back: Pick<BackNav, "label" | "full">) => `${back.label}${backExtra(back)}に戻る`;

/**
 * 題の左上の「‹ 行き先の名前」。名前が長いときは1行で …（最大は本文の幅の 60%）。全部の名前は title と読み上げ（「○○に戻る」）。
 * 読み上げの「に戻る」は aria-label でなく中の見えない文字で付ける（aria-label にすると、行き先の名前に「工事名」などが入ったとき、欄を名前で探す仕組みが取り違える）。
 * 名前と「に戻る」は同じかたまりの中に並べる（別のかたまりにすると読み上げの名前が「見積書 に戻る」と空白入りになる）
 * back を渡すと同じ行き先で「やめる」などと確かめを共用できる（渡さなければ fallback・dirty から作る）
 * PC版の設定の2列（#153、docs/62 §6）で左の列に行がある画面は描かない（左の列が戻り道）。その下の画面（口座の追加など）は今どおり
 */
export function BackButton({ fallback, dirty, back: given, light, iconOnly = false, compact = false }: { fallback?: BackTarget; dirty?: boolean; back?: BackNav; /** 藍の帯の中（白の字） */ light?: boolean; /** 「‹」だけ（行き先の名前は読み上げと乗せたときの文字だけ。見積依頼書の頭の帯＝#220 Figma ②改2） */ iconOnly?: boolean; /** 小さい「‹ 行き先」（帳票の頭。B-4 の DocHead に移すまで。#240） */ compact?: boolean }) {
  const own = useBackNav(fallback ?? { to: "/home", label: HOME_MSG.menu }, dirty);
  const pane = useSettingsPane();
  if (pane.top) return null;
  const back = given ?? own;
  // 乗せたときと読み上げは全部の名前（{project}の詳細なら「案件 27年度 …に戻る」。一覧・書類は短い名前のまま）
  const extra = backExtra(back);
  return (
    <>
      <Link to={back.to} className={"btn text backbtn" + (iconOnly ? " is-icon" : compact ? "" : " is-c") + (light ? " light" : "")} onClick={back.request} title={`${back.label}${extra}に戻る`} data-testid="back">
        {/* ‹（スマホ版・767px 以下と「‹」だけの形）／←（C案） */}
        <svg className="bkchev" viewBox="0 0 24 24" aria-hidden="true">
          <path d="M15 18l-6-6 6-6" />
        </svg>
        <svg className="bkarrow" viewBox="0 0 24 24" aria-hidden="true">
          <path d="M19 12H5M11 6l-6 6 6 6" />
        </svg>
        {back.area && !iconOnly && !compact ? <AreaIcon a={back.area} className="bkicon" /> : null}
        <span className={iconOnly ? "bkl vh" : "bkl"}>
          <span className="bkt">{back.label}</span>
          <span className="bksr">{extra}に戻る</span>
        </span>
      </Link>
      {given ? null : back.dialog}
    </>
  );
}
